import { isValidElement, type ReactNode } from "react";
import { SlotPlaceholder } from "./SlotPlaceholder";
import { getSlotDescriptor } from "./slotRegistry";
import type { TemplateMap } from "./TemplatesContext";

/**
 * Where an embedder's parsed templates project each slot id. Built once at
 * widget init by walking every parsed `<template id="lastbot-…">` for
 * `<slot name="X">` placeholders.
 *
 * Drives v2's two contracts:
 *
 *  1. **Cross-template projection** — a slot referenced in *any* embedder
 *     template, regardless of which `<Templated id>` shell encloses its
 *     default position, gets projected to the embedder's claimed location.
 *
 *  2. **Strict drop-on-override** — when a slot is claimed somewhere other
 *     than the active template scope, its default position is suppressed.
 *     The embedder owns explicit placement; the widget never "helpfully"
 *     double-renders.
 *
 * A slot may be claimed by MULTIPLE templates. This is the legitimate pattern
 * for composite/cross-page slots like `navbar`, `chat`, `welcome` — each
 * page template that wants those declares `<slot name="X">`. The claim
 * resolves correctly inside any of the claiming templates; default-position
 * rendering is suppressed only in scopes that ARE NOT among the claimants.
 *
 * Atomic slots (display / interactive kinds) generally have a single
 * rendering location — claiming them in multiple templates still works but
 * produces a dev-mode warning, because it's usually a copy-paste mistake.
 * Container / page kinds are silently allowed to multi-claim.
 *
 * The claim map is consumed at render time by `Slot` (via
 * `useSlotClaimedElsewhere`) and by `Templated` when projecting harvested
 * content into placeholders.
 */
export type SlotClaimMap = ReadonlyMap<string, ReadonlySet<string>>;

/**
 * Walk every parsed embedder template and record, per slot id, the set of
 * template ids that claim it.
 */
export const buildSlotClaimMap = (templates: TemplateMap): SlotClaimMap => {
  const claims = new Map<string, Set<string>>();
  for (const [templateId, tree] of templates) {
    walkForClaims(tree, templateId, claims);
  }
  return claims;
};

const walkForClaims = (
  node: ReactNode,
  templateId: string,
  claims: Map<string, Set<string>>,
): void => {
  if (node === null || node === undefined || typeof node === "boolean") return;
  if (typeof node === "string" || typeof node === "number") return;
  if (Array.isArray(node)) {
    for (const child of node) walkForClaims(child, templateId, claims);
    return;
  }
  if (!isValidElement(node)) return;

  if (node.type === SlotPlaceholder) {
    const name = (node.props as { name: string }).name;
    let set = claims.get(name);
    if (!set) {
      set = new Set();
      claims.set(name, set);
    }
    if (!set.has(templateId)) {
      // Warn only when an atomic-kind slot ends up in more than one
      // template (likely a copy-paste mistake). Container / page slots
      // legitimately appear in many page templates — navbar in every
      // page, chat in main surfaces, welcome wherever the embedder
      // wants the pre-chat chrome.
      if (set.size > 0) {
        const descriptor = getSlotDescriptor(name);
        const kind = descriptor?.kind;
        if (kind === "display" || kind === "interactive") {
          const others = Array.from(set).map(t => `"${t}"`).join(", ");
          console.warn(
            `[lastbot-chat] slot "${name}" (kind: ${kind}) claimed in multiple ` +
              `templates (${others} and "${templateId}"). Atomic slots usually ` +
              `belong to a single template — check for a copy-paste mistake. ` +
              `Container / page slots like navbar are exempt from this warning.`,
          );
        }
      }
      set.add(templateId);
    }
    // Continue descending so nested placeholders (e.g. composed wrapper
    // templates) are also discovered.
    const fallback = (node.props as { fallback?: ReactNode }).fallback;
    if (fallback !== undefined) {
      walkForClaims(fallback, templateId, claims);
    }
    return;
  }

  const props = node.props as { children?: ReactNode };
  if (props.children !== undefined) {
    walkForClaims(props.children, templateId, claims);
  }
};

/**
 * Predicate used by `<Slot>` at render time: is this slot id claimed by a
 * template *other than* the one currently rendering? If so, the slot should
 * be suppressed at its default position — content will project to the
 * claimed location instead.
 *
 * Returns false when:
 *   - The slot id isn't claimed anywhere (default rendering).
 *   - The slot id is claimed by the *currently-active* template (in-template
 *     harvest + project; no drop). Multi-claim case: the active template
 *     being ONE of several claimants is sufficient.
 *
 * Returns true when:
 *   - The slot is claimed only by templates OTHER than the active one — the
 *     consumer should not render.
 */
export const isSlotClaimedElsewhere = (
  claims: SlotClaimMap,
  slotId: string,
  activeTemplateId: string | null,
): boolean => {
  const set = claims.get(slotId);
  if (set === undefined || set.size === 0) return false;
  if (activeTemplateId === null) return true;
  return !set.has(activeTemplateId);
};
